'use client'

import { Select as SelectPrimitive } from '@base-ui/react/select'
import { cn } from '@nt/ui/cn'
import { inputVariants } from '@nt/ui/field'
import { selectRecipe } from '@nt/ui/recipes'
import { cva, type VariantProps } from 'class-variance-authority'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import * as React from 'react'

// Compatibility wrapper: adapts Base UI's onValueChange (value | null, eventDetails)
// to the simpler Radix-style (value) signature used throughout the codebase
function Select({
  onValueChange,
  ...props
}: Omit<SelectPrimitive.Root.Props<string>, 'onValueChange'> & {
  onValueChange?: (value: string) => void
}) {
  return (
    <SelectPrimitive.Root
      onValueChange={
        onValueChange
          ? (value: string | null) => {
              if (value !== null) onValueChange(value)
            }
          : undefined
      }
      {...props}
    />
  )
}

function SelectGroup({ className, ...props }: SelectPrimitive.Group.Props) {
  return <SelectPrimitive.Group data-slot="select-group" className={cn('scroll-my-1 p-1', className)} {...props} />
}

function SelectValue({ className, ...props }: SelectPrimitive.Value.Props) {
  return (
    <SelectPrimitive.Value data-slot="select-value" className={cn('flex flex-1 text-left', className)} {...props} />
  )
}

// Reads as a field: the @nt/ui control look (control height, the 3:1
// `border-input` edge, the forest focus ring). The variant names are
// pinned (design-system-variants-forms.md); none lightens the edge.
const selectTriggerVariants = cva(
  `group/select-trigger ${inputVariants({ kind: 'input' })} ${selectRecipe.trigger} pr-3 data-[size=sm]:h-8 data-[size=sm]:px-2.5 data-[size=sm]:text-sm`,
  {
    variants: {
      variant: {
        default: '',
        emphasis: 'hover:border-primary',
        brand: 'hover:border-primary',
      },
    },
    defaultVariants: { variant: 'default' },
  },
)

function SelectTrigger({
  className,
  size = 'default',
  variant,
  children,
  ...props
}: SelectPrimitive.Trigger.Props &
  VariantProps<typeof selectTriggerVariants> & {
    size?: 'sm' | 'default'
  }) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(selectTriggerVariants({ variant, className }))}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon
        render={
          <ChevronDownIcon className="pointer-events-none size-4 text-muted-foreground transition-transform duration-150 group-data-popup-open/select-trigger:rotate-180" />
        }
      />
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({
  className,
  children,
  side = 'bottom',
  sideOffset = 6,
  align = 'start',
  alignOffset = 0,
  // A list that opens over its own field hides what was chosen and reads as
  // the operating system's menu (owner 2026-09-25): it drops below instead.
  alignItemWithTrigger = false,
  ...props
}: SelectPrimitive.Popup.Props &
  Pick<SelectPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset' | 'alignItemWithTrigger'>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        className="isolate z-50"
      >
        <SelectPrimitive.Popup
          data-slot="select-content"
          data-align-trigger={alignItemWithTrigger}
          className={cn(
            selectRecipe.content,
            'relative isolate max-h-(--available-height) w-(--anchor-width) data-[align-trigger=true]:animate-none',
            className,
          )}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List>{children}</SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({ className, ...props }: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel data-slot="select-label" className={cn(selectRecipe.label, className)} {...props} />
  )
}

function SelectItem({
  className,
  children,
  tone,
  ...props
}: SelectPrimitive.Item.Props & { tone?: 'default' | 'brand' }) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        selectRecipe.item,
        'w-full last:[span]:*:flex last:[span]:*:items-center last:[span]:*:gap-2',
        tone === 'brand' && 'cursor-pointer hover:bg-success-tint/60',
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText className="flex min-w-0 flex-1 gap-2 whitespace-nowrap">
        {children}
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator render={<span className={selectRecipe.indicator} />}>
        <CheckIcon className="pointer-events-none" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({ className, ...props }: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn(selectRecipe.separator, 'pointer-events-none', className)}
      {...props}
    />
  )
}

function SelectScrollUpButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn(selectRecipe.scrollButton, 'top-0', className)}
      {...props}
    >
      <ChevronUpIcon />
    </SelectPrimitive.ScrollUpArrow>
  )
}

function SelectScrollDownButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn(selectRecipe.scrollButton, 'bottom-0', className)}
      {...props}
    >
      <ChevronDownIcon />
    </SelectPrimitive.ScrollDownArrow>
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
